import type { AppKind } from './roles';

/**
 * Single source of truth for every deploy-pipeline byte cap. Imported by:
 *   • Console wizard (FileBundlePicker, NewAppWizard, About page)
 *   • CP /deploys handler (workers/control-plane/src/api/deploys.ts)
 *   • MCP deploy-session DO + add_files tool (workers/mcp/...)
 *   • CP /api/v1/limits handler — so clients can re-fetch at runtime
 *     and pick up server-side bumps without a rebuild
 */

/** Per-file cap inside a deploy bundle. */
export const MAX_FILE_BYTES = 100 * 1024 * 1024;
/** Total bundle cap, summed across files post-decode. */
export const MAX_BUNDLE_BYTES = 500 * 1024 * 1024;
/** Zipped wire cap on the MCP staging-bucket PUT. */
export const MAX_ZIPPED_BUNDLE_BYTES = 500 * 1024 * 1024;
/** Zip-bomb defense: total decompressed bytes from any single upload. */
export const MAX_DECOMPRESSED_BUNDLE_BYTES = 2 * 1024 * 1024 * 1024;
/** Hard cap on how many files a single deploy may contain. */
export const MAX_FILE_COUNT = 1000;

/**
 * Structured shape returned by GET /api/v1/limits. Versioned via
 * additive-only changes — existing fields never shrink in meaning.
 */
export interface DeployLimits {
  readonly max_file_bytes: number;
  readonly max_bundle_bytes: number;
  readonly max_zipped_bundle_bytes: number;
  readonly max_decompressed_bundle_bytes: number;
  readonly max_file_count: number;
}

export const DEPLOY_LIMITS: DeployLimits = {
  max_file_bytes: MAX_FILE_BYTES,
  max_bundle_bytes: MAX_BUNDLE_BYTES,
  max_zipped_bundle_bytes: MAX_ZIPPED_BUNDLE_BYTES,
  max_decompressed_bundle_bytes: MAX_DECOMPRESSED_BUNDLE_BYTES,
  max_file_count: MAX_FILE_COUNT,
};

/** Returns the cap formatted as e.g. "25 MB". Used in UI hint strings. */
export function formatLimitMb(bytes: number): string {
  return `${Math.round(bytes / 1024 / 1024)} MB`;
}

export interface BundleInput {
  readonly kind: AppKind;
  readonly files: ReadonlyArray<{ path: string; size: number }>;
}

export interface ValidationResult {
  readonly blockers: ReadonlyArray<string>;
  readonly warnings: ReadonlyArray<string>;
}

function fmtMb(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

export function validateBundle(input: BundleInput): ValidationResult {
  const { kind, files } = input;
  const blockers: string[] = [];
  const warnings: string[] = [];

  if (files.length === 0) return { blockers, warnings };

  const seen = new Set<string>();
  for (const f of files) {
    if (seen.has(f.path)) {
      blockers.push(`Two files have the same name: ${f.path}. Rename or remove one.`);
    }
    seen.add(f.path);
  }

  for (const f of files) {
    if (f.size > MAX_FILE_BYTES) {
      blockers.push(
        `${f.path} is ${fmtMb(f.size)} — single files must be ${formatLimitMb(MAX_FILE_BYTES)} or smaller.`,
      );
    }
  }

  const total = files.reduce((s, f) => s + f.size, 0);
  if (total > MAX_BUNDLE_BYTES) {
    blockers.push(
      `Total upload is ${fmtMb(total)} — the limit is ${formatLimitMb(MAX_BUNDLE_BYTES)}.`,
    );
  }

  const htmlCount = files.filter((f) => /\.html?$/i.test(f.path)).length;
  const jsCount = files.filter((f) => /\.m?js$/i.test(f.path)).length;
  const cssCount = files.filter((f) => /\.css$/i.test(f.path)).length;

  if (kind === 'static') {
    if (htmlCount === 0) {
      blockers.push('Upload an HTML file to get started.');
    }
    if (jsCount > 0 && htmlCount === 0 && cssCount === 0) {
      warnings.push('This looks like a JavaScript app. Go back and switch the type?');
    }
  } else {
    if (jsCount === 0) {
      blockers.push('Upload a JavaScript file to get started.');
    }
    if (htmlCount >= 2 || cssCount >= 1) {
      warnings.push('This looks like a website. Go back and switch the type?');
    }
  }

  return { blockers, warnings };
}
